import { z } from "zod"

import type { SocialPlatform } from "./ports"

const INSTAGRAM_HOSTS = new Set(["instagram.com", "www.instagram.com"])
const FACEBOOK_HOSTS = new Set(["facebook.com", "www.facebook.com", "m.facebook.com"])
const INSTAGRAM_RESERVED_PATHS = new Set([
  "accounts",
  "direct",
  "explore",
  "p",
  "reel",
  "reels",
  "stories",
  "tv",
])

export const SocialProfileUrlSchema = z.string().trim().url().transform((value, context) => {
  try {
    return normalizeSocialProfileUrl(value)
  } catch (error) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: error instanceof Error ? error.message : "Enter a valid social profile URL.",
    })
    return z.NEVER
  }
})

export function normalizeSocialProfileUrl(value: string): string {
  const url = new URL(value.trim())
  url.hash = ""
  url.search = ""
  const platform = getSocialPlatform(url)

  switch (platform) {
    case "instagram":
      return normalizeInstagramProfileUrl(url)
    case "facebook":
      return normalizeFacebookProfileUrl(url)
    default: {
      const exhaustivePlatform: never = platform
      return exhaustivePlatform
    }
  }
}

export function getSocialPlatform(value: URL | string): SocialPlatform {
  const url = typeof value === "string" ? new URL(value) : value
  const hostname = url.hostname.toLowerCase()
  if (INSTAGRAM_HOSTS.has(hostname)) return "instagram"
  if (FACEBOOK_HOSTS.has(hostname)) return "facebook"
  throw new Error("Use an Instagram or Facebook profile URL.")
}

export function getInstagramHandle(profileUrl: string): string {
  const url = new URL(profileUrl)
  if (getSocialPlatform(url) !== "instagram") {
    throw new Error("Expected an Instagram profile URL.")
  }
  const [handle] = getPathParts(url)
  if (!handle || INSTAGRAM_RESERVED_PATHS.has(handle.toLowerCase())) {
    throw new Error("Enter an Instagram profile URL, not a post or app page.")
  }
  return handle
}

function normalizeInstagramProfileUrl(url: URL): string {
  const handle = getInstagramHandle(url.toString())
  return `https://www.instagram.com/${handle}/`
}

function normalizeFacebookProfileUrl(url: URL): string {
  const parts = getPathParts(url)
  if (parts.length === 0) {
    throw new Error("Enter a Facebook Page or profile URL.")
  }
  return `https://www.facebook.com/${parts.map(encodeURIComponent).join("/")}/`
}

function getPathParts(url: URL): string[] {
  return url.pathname
    .split("/")
    .map(part => decodeURIComponent(part).trim())
    .filter(Boolean)
}
